import classnames from 'classnames';
import LeftArrowIcon from '@/components/Icons/LeftArrow';
import React, { Fragment, PropsWithChildren, memo, useCallback, useState } from 'react';
import Header from './Header';
import Span from './Span';
import Link from './Link';
import Button from './Button';
import Container from './Container';
import Div from './Div';
import useListenWindowEvent from '@/hooks/useListenWindowEvent';

const DELTA = 15;

type AppBarProps = {
  className?: string;
  label?: string;
  url?: string | null;
  onClick?: () => void;
  onScrollLabel?: string;
  backIconColor?: string;
};

type AppBarWrapperProps = {
  transparent?: boolean;
  fixed?: boolean;
  wrapperClassName?: string;
  className?: string;
  render?: (isScrolled: boolean) => React.ReactNode;
  preventScrollBackground?: boolean;
};
const AppBarWrapper = ({
  render,
  fixed,
  transparent,
  wrapperClassName,
  preventScrollBackground,
  className,
}: PropsWithChildren<AppBarWrapperProps>) => {
  const [wrapperClasses, setWrapperClasses] = useState<string>('');

  const listener = useCallback(
    (ev: Event) => {
      const { scrollY } = window;
      let classes = '';
      if (fixed && transparent && scrollY > DELTA && !preventScrollBackground) {
        classes = 'bg-white transition-all shadow-subtle';
      }
      setWrapperClasses(classes);
    },
    [transparent, fixed, preventScrollBackground],
  );

  useListenWindowEvent('scroll', listener, 10);
  return (
    <Div
      className={classnames(
        'z-[2]',
        {
          'fixed left-0 right-0': fixed,
          'bg-transparent': transparent,
          'bg-white': !transparent,
        },
        wrapperClassName,
        wrapperClasses,
      )}
    >
      <Container className={classnames('flex gap-s4 py-5', className)}>
        {render && render(Boolean(wrapperClasses))}
      </Container>
    </Div>
  );
};

const AppBar = ({
  label,
  url,
  onClick,
  onScrollLabel,
  backIconColor,
  ...restProps
}: PropsWithChildren<AppBarProps & AppBarWrapperProps>) => {
  const children = (isScrolled: boolean) => (
    <Fragment>
      <Span className="flex gap-s4">
        <LeftArrowIcon fill={backIconColor || '#424242'} />
      </Span>
      <Header>{isScrolled && onScrollLabel ? onScrollLabel : label}</Header>
    </Fragment>
  );

  if (url) {
    return (
      <AppBarWrapper
        {...restProps}
        render={(isScrolled) => (
          <Link href={url} className="flex gap-s4">
            {children(isScrolled)}
          </Link>
        )}
      />
    );
  }

  return (
    <AppBarWrapper
      {...restProps}
      render={(isScrolled) => (
        <Button theme="none" className="gap-s4" onClick={onClick}>
          {children(isScrolled)}
        </Button>
      )}
    />
  );
};

export default memo(AppBar);
